import type { ChangeEventHandler, KeyboardEventHandler } from 'react';
import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import { getObjectTitle, getTestProps } from '../../lib/helpers';

import { useContentTypes, useSearch } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useDebounceCallback from '../../hooks/useDebounceCallback';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import Input from '../Input/Input';
import Loader from '../Loader/Loader';
import SearchResultTile from './SearchResultTile';

const positiveModulo = function (value: number, divider: number) {
  const result = ((value % divider) + divider) % divider;
  return isNaN(result) ? 0 : result;
};

interface GlobalSearchModalProps {
  /**
   * Test id for button
   */
  testId?: string;
}

const GlobalSearchModal = ({ testId = '' }: GlobalSearchModalProps) => {
  const { t } = useTranslation();
  const [selectedResultIdx, setSelectedResultIdx] = useState(0);
  const [searchPhrase, setSearchPhrase] = useState('');
  const modalInstance = use(ModalInstanceContext);
  const navigate = useNavigate();
  const { buildUrlWithSpace } = useSelectedSpace();

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => setSearchPhrase(e.target.value),
    [],
  );

  const debouncedSearchChange = useDebounceCallback(handleSearchChange, 250);

  const ctdParams = useMemo(
    () => ({
      label: searchPhrase,
      limit: 5,
    }),
    [searchPhrase],
  );
  const searchParams = useMemo(
    () => ({
      q: searchPhrase,
      limit: 10,
    }),
    [searchPhrase],
  );

  const hookOptions = useMemo(
    () => ({
      pause: searchPhrase.length < 3,
    }),
    [searchPhrase],
  );

  const {
    data: ctdData,
    isLoading: isLoadingCtdSearch,
    errors: ctdSearchErrors,
  } = useContentTypes(ctdParams, hookOptions);
  const {
    data: searchData,
    isLoading: isSearching,
    errors: ctoSearchErrors,
  } = useSearch(searchParams, hookOptions);

  const ctdNamesParams = useMemo(
    () => [
      {
        names: searchData?.map(({ item }) => item?.internal?.contentType),
      },
      { pause: !searchData?.length },
    ],
    [searchData],
  );

  const {
    data: ctdsByName,
    errors: ctdDataErrors,
    isLoading: isLoadingCtdForResults,
  } = useContentTypes(...ctdNamesParams);

  const contentTypesByName = useMemo(
    () =>
      ctdsByName.reduce(
        (ctds, ctd) => {
          ctds[ctd.name] = ctd;
          return ctds;
        },
        {} as Record<string, ContentTypeDefinition>,
      ),
    [ctdsByName],
  );

  useApiErrorsToast(ctdSearchErrors);
  useApiErrorsToast(ctoSearchErrors);
  useApiErrorsToast(ctdDataErrors);

  const filteredCtd = useMemo(() => {
    if (!ctdData?.length) return ctdData;

    return ctdData.filter((ctd) => !ctd.internal || ctd.name === '_media');
  }, [ctdData]);

  const filteredSearchResults = useMemo(() => {
    if (!searchData?.length) return searchData;
    return searchData.filter((searchResult) => {
      return (
        contentTypesByName &&
        (!contentTypesByName[searchResult.item.internal.contentType]
          ?.internal ||
          contentTypesByName[searchResult.item.internal.contentType]?.name ===
            '_media')
      );
    });
  }, [contentTypesByName, searchData]);

  const handleResultClick = useCallback(
    (type: string, idx: number) => {
      let resultUrl;
      if (type === 'ctd') {
        if (!filteredCtd[idx]) return;

        resultUrl =
          filteredCtd[idx].name === '_media'
            ? buildUrlWithSpace('media')
            : buildUrlWithSpace(
                `content-type-objects/${filteredCtd[idx].name}`,
              );
      } else if (type === 'object') {
        if (!filteredSearchResults[idx]) return;

        const typeName = filteredSearchResults[idx].item.internal.contentType;

        if (typeName === '_media') {
          window.open(getMediaUrl(filteredSearchResults[idx].item), '_blank');
        } else {
          resultUrl = buildUrlWithSpace(
            `content-type-objects/edit/${typeName}/${filteredSearchResults[idx].item.id}`,
          );
        }
      }

      if (resultUrl) navigate(resultUrl);
      modalInstance.resolve?.(false);
    },
    [
      filteredCtd,
      modalInstance,
      buildUrlWithSpace,
      navigate,
      filteredSearchResults,
    ],
  );
  const handleSearchKeyDown = useCallback<
    KeyboardEventHandler<HTMLInputElement>
  >(
    (e) => {
      if (
        ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].indexOf(e.key) ===
          -1 ||
        e.ctrlKey ||
        e.metaKey
      )
        return;

      if (e.shiftKey && e.key !== 'Tab') return;

      e.preventDefault();

      if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey))
        setSelectedResultIdx((idx) =>
          positiveModulo(
            idx + 1,
            filteredCtd.length + filteredSearchResults.length,
          ),
        );
      if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey))
        setSelectedResultIdx((idx) =>
          positiveModulo(
            idx - 1,
            filteredCtd.length + filteredSearchResults.length,
          ),
        );
      if (e.key === 'Enter') {
        if (selectedResultIdx < filteredCtd.length)
          handleResultClick('ctd', selectedResultIdx);
        else
          handleResultClick('object', selectedResultIdx - filteredCtd.length);
      }
      if (e.key === 'Escape') {
        modalInstance.resolve?.(false);
      }
    },
    [
      filteredCtd.length,
      handleResultClick,
      modalInstance,
      filteredSearchResults.length,
      selectedResultIdx,
    ],
  );

  const hasCTDResults = !!filteredCtd?.length && !isLoadingCtdSearch;
  const hasCTOResults = !!filteredSearchResults?.length && !isSearching;

  const isLoading = isSearching || isLoadingCtdSearch || isLoadingCtdForResults;
  const hasNoResults = !isLoading && !hasCTDResults && !hasCTOResults;

  return (
    <div {...getTestProps(testId)}>
      <Input
        type="search"
        value={searchPhrase}
        onChange={debouncedSearchChange}
        label={t('Global.Search.InputLabel')}
        placeholder={t('Global.Search.InputPlaceholder')}
        autoFocus
        tabIndex={1}
        onKeyDown={handleSearchKeyDown}
        additionalClasses={'mb-4'}
        {...getTestProps(testId, 'searchbox', 'testId')}
      />
      {isLoading && searchPhrase.length >= 3 && (
        <Loader size="small" type="spinner-grid" additionalClasses="mx-auto" />
      )}
      {hasNoResults && searchPhrase.length >= 3 && t('Global.Search.NoResults')}
      <div className="max-h-[calc(100vh-15rem)] overflow-auto mt-2">
        {hasCTDResults && (
          <div {...getTestProps(testId, 'ctd-results')}>
            {t('Global.Search.ContentTypesHeader')}:
            {filteredCtd.map((ctd, idx) => (
              <SearchResultTile
                key={ctd.id}
                selected={selectedResultIdx === idx}
                onClick={() => handleResultClick('ctd', idx)}
                ctdLabel={ctd.label || ctd.name}
                ctdName={ctd.name}
              />
            ))}
          </div>
        )}
        {hasCTOResults && (
          <div {...getTestProps(testId, 'cto-results')}>
            {t('Global.Search.ContentObjectsHeader')}:
            <div className="space-y-2">
              {filteredSearchResults.map((object, idx) => {
                const objectProperties = Object.keys(object.item)
                  .filter(
                    (key) =>
                      [
                        'internal',
                        '@timestamp',
                        'id',
                        'object_data',
                        'content_type_definition_id',
                        'organization_id',
                        'object_id',
                        'created_at',
                        'deleted_at',
                        'updated_at',
                      ].indexOf(key) === -1,
                  )
                  .filter(
                    (key) =>
                      typeof object.item[key as keyof typeof object.item] !==
                      'object',
                  )
                  .filter(
                    (key) => !!object.item[key as keyof typeof object.item],
                  )
                  .map(
                    (key) =>
                      `${key}: ${object.item[key as keyof typeof object.item]}`,
                  )
                  .join(' ● ');
                return (
                  <SearchResultTile
                    key={object.item.id}
                    title={getObjectTitle(
                      object.item,
                      contentTypesByName?.[object.item?.internal.contentType],
                    )}
                    selected={selectedResultIdx === idx + filteredCtd.length}
                    onClick={() => handleResultClick('object', idx)}
                    image={
                      object?.item?.internal?.contentType === '_media' &&
                      object.item.type === 'image'
                        ? getMediaUrl(object.item, 150)
                        : undefined
                    }
                    ctdName={object.item?.internal.contentType}
                    ctdLabel={
                      contentTypesByName?.[object.item?.internal.contentType]
                        ?.label || object.item?.internal.contentType
                    }
                    properties={objectProperties}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchModal;
